<template>
  <!-- 수신자 분포 (발행 기준) — REQ-MOD-4 + 집계 기준/단위 전환 컨트롤 -->
  <div class="column-box demographics-widget">
    <div class="demographics-widget-header">
      <div class="demographics-header-top">
        <h3>{{ $t('dashboard_demographics_title') }}</h3>
        <span v-if="!loading && !error" class="demographics-widget-note">
          {{ $t('dashboard_demographics_header_note', { count: formatCommaNumber(totalCount) }) }}
        </span>
      </div>
      <div class="demographics-controls">
        <div class="demographics-control-group">
          <span class="demographics-control-label">{{ $t('dashboard_demographics_control_basis_label') }}</span>
          <a-radio-group :value="mode" size="small" @change="onModeChange">
            <a-radio-button value="birthYear">{{ $t('dashboard_demographics_mode_birth_year') }}</a-radio-button>
            <a-radio-button value="issueAge">{{ $t('dashboard_demographics_mode_issue_age') }}</a-radio-button>
            <a-radio-button value="currentAge">{{ $t('dashboard_demographics_mode_current_age') }}</a-radio-button>
          </a-radio-group>
        </div>
        <div class="demographics-control-group">
          <span class="demographics-control-label">{{ $t('dashboard_demographics_control_bucket_label') }}</span>
          <a-radio-group :value="bucketRadioValue" size="small" @change="onBucketSizeChange">
            <a-radio-button :value="1">{{ $t('dashboard_demographics_bucket_1') }}</a-radio-button>
            <a-radio-button :value="5">{{ $t('dashboard_demographics_bucket_5') }}</a-radio-button>
            <a-radio-button :value="10">{{ $t('dashboard_demographics_bucket_10') }}</a-radio-button>
            <a-radio-button value="custom">{{ $t('dashboard_demographics_bucket_custom') }}</a-radio-button>
          </a-radio-group>
          <!-- REQ 확장: 기간 필터의 [프리셋 + 직접선택] 패턴과 동일하게, "직접설정" 선택 시에만
               인라인 드롭다운을 노출한다(조회기간의 a-range-picker와 동일한 등장 방식).
               칩과 동일한 size="small" 한 줄짜리 컨트롤이라 높이가 자연스럽게 맞는다. -->
          <a-select
            v-if="showCustomInput"
            v-model:value="customBucketDraft"
            size="small"
            class="demographics-custom-bucket-select"
            @change="onCustomBucketCommit"
          >
            <a-select-option v-for="n in 50" :key="n" :value="n">
              {{ n }}{{ $t('dashboard_demographics_bucket_year_suffix') }}
            </a-select-option>
          </a-select>
        </div>
        <div class="demographics-control-group demographics-control-group--exclude-unknown">
          <!-- Ant Design Vue 4 Checkbox에는 size prop이 없어 CSS로 기존 size small
               컨트롤(demographics-control-label 등)과 시각적 크기를 맞춘다. -->
          <a-checkbox v-model:checked="localExcludeUnknown">
            {{ $t('dashboard_demographics_exclude_unknown') }}
          </a-checkbox>
        </div>
      </div>
    </div>
    <DashboardWidgetState
      :loading="loading"
      :error="error"
      :is-empty="isEmpty"
      :empty-message="emptyMessage"
      @retry="$emit('retry')"
    >
      <div class="demographics-chart-wrap">
        <canvas ref="canvasRef" />
      </div>
      <div class="demographics-footer">
        <span>{{ $t('dashboard_demographics_footer_basis') }}</span>
        <a-tooltip :title="$t('dashboard_demographics_footer_unknown_def')">
          <i class="fa-regular fa-circle-info" />
        </a-tooltip>
      </div>
    </DashboardWidgetState>
  </div>
</template>

<script>
import { markRaw } from 'vue';
import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import DashboardWidgetState from './DashboardWidgetState.vue';
import { formatCommaNumber } from '@/utils/utils';

// REQ-MOD-4 U-4.6: 색각이상에서도 구분 가능한 색 조합
const MALE_COLOR = '#4A90D9';
const FEMALE_COLOR = '#F5A623';
const UNKNOWN_COLOR = '#ACB2B9';

// 1년 단위는 50+ 막대가 나올 수 있어 얇은 막대 + 데이터라벨 숨김(툴팁만)으로 전환
const DENSE_BUCKET_SIZE = 1;

// 프리셋 단위 목록(이 외의 값은 모두 "직접설정"으로 간주)
const PRESET_BUCKET_SIZES = [1, 5, 10];

export default {
  name: 'DemographicsChart',
  components: { DashboardWidgetState },
  emits: ['retry', 'params-change', 'exclude-unknown-change'],
  props: {
    buckets: { type: Array, default: () => [] },
    unknownCount: { type: Number, default: 0 },
    totalCount: { type: Number, default: 0 },
    // 집계 기준: birthYear(년도, 기본) / issueAge(발행 당시 나이) / currentAge(현재 나이)
    mode: { type: String, default: 'birthYear' },
    // 집계 단위(년): 1 / 5(기본) / 10
    bucketSize: { type: Number, default: 5 },
    // '미상 제외' 체크박스 초기값(localStorage prefs 하이드레이션, 기본 체크됨).
    // mode/bucketSize와 달리 재조회 파라미터가 아니므로 최초 1회만 하이드레이션하고
    // 이후에는 로컬 상태(localExcludeUnknown)로 관리한다.
    excludeUnknown: { type: Boolean, default: true },
    loading: { type: Boolean, default: false },
    error: { type: Boolean, default: false },
    isEmpty: { type: Boolean, default: false },
    emptyMessage: { type: String, default: '' },
  },
  data() {
    return {
      formatCommaNumber,
      // 직접설정 드롭다운의 표시 값. "처음 열릴 때 기본값 = 현재 bucketSize"이므로
      // 프리셋/직접 여부와 무관하게 항상 현재 bucketSize로 시작한다.
      customBucketDraft: this.bucketSize,
      // "직접설정" 칩을 눌러 드롭다운을 열어둔 상태(선택 즉시 커밋되므로 값이 바뀌지
      // 않는 한 bucketSize prop은 그대로일 수 있어, prop만으로는 노출 여부를 판단할 수 없다).
      customChipSelected: !PRESET_BUCKET_SIZES.includes(this.bucketSize),
      localExcludeUnknown: this.excludeUnknown,
      // scheduleRender() 중복 호출 방지 플래그. 같은 nextTick 틱 안에서 여러 watcher가
      // 동시에 트리거되어도(buckets+unknownCount+loading 등) renderChart()는 1회만 실행한다.
      renderPending: false,
    };
  },
  created() {
    // 근본 수정: chart.js 인스턴스를 data()에 두면 Vue 3가 깊은 반응형 프록시로 감싸고,
    // chart.js 옵션 리졸버가 그 프록시를 순회하다 무한 재귀(Maximum call stack size
    // exceeded)에 빠진다. data() 밖에서 대입되는 인스턴스 속성은 반응형이 아니므로
    // 여기서 초기화한다(생성 시점에는 markRaw로 한 번 더 방어한다).
    this.chartInstance = null;
  },
  computed: {
    // 현재 bucketSize가 프리셋(1/5/10)이 아니면 "직접설정"이 활성 상태(서버에 커밋된 상태 기준)
    isCustomBucket() {
      return !PRESET_BUCKET_SIZES.includes(this.bucketSize);
    },
    // 입력창 노출 여부: 커밋된 값이 이미 직접값이거나, 칩을 눌러 아직 커밋 전인 경우 모두 포함
    showCustomInput() {
      return this.isCustomBucket || this.customChipSelected;
    },
    // a-radio-group에 표시할 값: 프리셋이면 그 숫자, 아니면 'custom' 칩을 선택 상태로
    bucketRadioValue() {
      return this.showCustomInput ? 'custom' : this.bucketSize;
    },
  },
  watch: {
    // QA 버그 수정: 모든 재렌더 트리거를 scheduleRender()로 통일한다. DashboardWidgetState가
    // loading/isEmpty 상태에 따라 canvas를 마운트/언마운트하므로, DOM 패치 전에 렌더하면
    // this.$refs.canvasRef가 최신 상태가 아니어서 빈 화면으로 남을 수 있다.
    // (회귀 수정) 같은 fetch 완료 시점에 buckets/unknownCount/loading이 동시에 바뀌면
    // 각자 $nextTick(renderChart)를 걸어 같은 틱에 renderChart()가 여러 번 실행되고,
    // 두 번째 호출부터 "warm"(destroy 후 재생성) 경로를 타 빈 캔버스로 남았다 —
    // scheduleRender()가 같은 틱 안의 중복 호출을 1회로 합친다.
    buckets() {
      this.scheduleRender();
    },
    unknownCount() {
      this.scheduleRender();
    },
    loading(newVal) {
      if (!newVal) this.scheduleRender();
    },
    // E-3.1: 로컬 상태만으로 재렌더. API 재조회·params-change emit 없음.
    // E-3.3: 전용 영속화 이벤트만 방출한다(params-change 채널 재사용 금지).
    localExcludeUnknown(newVal) {
      this.scheduleRender();
      this.$emit('exclude-unknown-change', newVal);
    },
  },
  mounted() {
    this.renderChart();
  },
  beforeUnmount() {
    if (this.chartInstance) this.chartInstance.destroy();
  },
  methods: {
    // (회귀 수정) 같은 nextTick 틱 안에서 여러 watcher가 동시에 트리거되어도
    // renderChart()는 1회만 실행되도록 예약을 합친다.
    scheduleRender() {
      if (this.renderPending) return;
      this.renderPending = true;
      this.$nextTick(() => {
        this.renderPending = false;
        this.renderChart();
      });
    },
    // S-3.1/S-3.2: 체크(제외) 시 분모 = totalCount - unknownCount, 해제 시 분모 = totalCount(현행).
    // N-3.1: 유효 분모가 0이면 나누기 오류 없이 0을 반환한다.
    percentage(count) {
      const denominator = this.localExcludeUnknown
        ? this.totalCount - this.unknownCount
        : this.totalCount;
      if (!denominator) return 0;
      return (count / denominator) * 100;
    },
    onModeChange(e) {
      this.$emit('params-change', { mode: e.target.value, bucketSize: this.bucketSize });
    },
    onBucketSizeChange(e) {
      const value = e.target.value;
      if (value === 'custom') {
        // 조회기간의 "직접선택"과 동일한 패턴: 칩 선택은 드롭다운만 노출하고,
        // 드롭다운에서 실제로 값을 고르기 전까지는 재조회하지 않는다.
        // 기본 표시값 = 현재 bucketSize(이미 직접값이면 그대로, 프리셋이면 그 값).
        this.customChipSelected = true;
        this.customBucketDraft = this.bucketSize;
        return;
      }
      this.customChipSelected = false;
      this.$emit('params-change', { mode: this.mode, bucketSize: value });
    },
    // a-select는 값을 고르면 즉시 커밋한다(디바운스 불필요 — 타이핑이 아닌 목록 선택이므로).
    onCustomBucketCommit(value) {
      this.customChipSelected = true;
      if (value === null || value === undefined || Number.isNaN(value)) return; // 빈 값은 무시, 마지막 유효 값 유지
      if (value === this.bucketSize) return;
      this.$emit('params-change', { mode: this.mode, bucketSize: value });
    },
    // 나이 기준일 때만 툴팁에 "세" 접미어를 붙인다(축 라벨은 노이즈를 줄이기 위해 그대로 둠).
    tooltipBucketLabel(label) {
      return this.mode === 'birthYear' ? label : `${label}${this.$i18n.t('dashboard_demographics_age_suffix')}`;
    },
    renderChart() {
      if (!this.$refs.canvasRef || this.loading || this.error || this.isEmpty) return;
      // QA 버그 수정: 렌더 도중 예외가 발생해도 크래시하거나 빈 캔버스로 방치되지 않도록
      // destroy + new Chart 전체를 try/catch로 감싼다.
      try {
        const isDense = this.bucketSize === DENSE_BUCKET_SIZE;
        const excludeUnknown = this.localExcludeUnknown;

        // REQ-MOD-4 U-4.4: 데이터 기반 버킷 + 맨 오른쪽 "미상" 단일 막대 (라벨 형식은 mode에 따라 백엔드가 결정)
        // [D3] '미상 제외' 체크 시 미상 라벨/후행 null을 제거해 labels 길이와 male/female 데이터
        // 길이를 일치시킨다(불일치 시 축·툴팁 인덱스가 어긋난다).
        const labels = excludeUnknown
          ? this.buckets.map((b) => b.bucketLabel)
          : [...this.buckets.map((b) => b.bucketLabel), this.$i18n.t('dashboard_demographics_unknown_label')];
        const maleData = excludeUnknown
          ? this.buckets.map((b) => b.male)
          : [...this.buckets.map((b) => b.male), null];
        const femaleData = excludeUnknown
          ? this.buckets.map((b) => b.female)
          : [...this.buckets.map((b) => b.female), null];
        const unknownData = [...this.buckets.map(() => null), this.unknownCount];

        const percentageOf = (value) => (value == null ? null : this.percentage(value));
        const barThickness = isDense ? 10 : 36;

        const datasets = [
          {
            label: this.$i18n.t('dashboard_demographics_male'),
            data: maleData,
            backgroundColor: MALE_COLOR,
            maxBarThickness: barThickness,
          },
          {
            label: this.$i18n.t('dashboard_demographics_female'),
            data: femaleData,
            backgroundColor: FEMALE_COLOR,
            maxBarThickness: barThickness,
          },
        ];
        // '미상 제외' 체크 시 미상 데이터셋 자체를 렌더하지 않는다(범례/막대 모두 미표시).
        if (!excludeUnknown) {
          datasets.push({
            label: this.$i18n.t('dashboard_demographics_unknown_label'),
            data: unknownData,
            backgroundColor: UNKNOWN_COLOR,
            maxBarThickness: barThickness,
          });
        }

        const chartOptions = {
            responsive: true,
            maintainAspectRatio: false,
            layout: { padding: { top: 20 } },
            interaction: { intersect: false, mode: 'index' },
            plugins: {
              legend: {
                // REQ-MOD-4 U-4.6: 범례 필수 노출
                display: true,
                position: 'top',
                align: 'end',
              },
              tooltip: {
                callbacks: {
                  // REQ-MOD-4 U-4.7: "1990-1994 남성: 1,790건 (20.3%)" / 나이 기준 "34세 남성: ..." 형식
                  label: (context) => {
                    const value = context.raw;
                    if (value === null || value === undefined) return null;
                    const pct = percentageOf(value).toFixed(1);
                    // [D1] '미상 제외' 체크 시 미상 라벨이 제거되어 마지막 인덱스가 실제 버킷이
                    // 되므로, "마지막 인덱스 = 미상" 특례를 excludeUnknown일 때는 적용하지 않는다.
                    const isUnknownIndex = !excludeUnknown
                      && context.dataIndex === context.chart.data.labels.length - 1;
                    const bucketLabel = isUnknownIndex
                      ? context.label
                      : this.tooltipBucketLabel(context.label);
                    return `${bucketLabel} ${context.dataset.label}: ${formatCommaNumber(value)}건 (${pct}%)`;
                  },
                },
              },
              datalabels: {
                // 1년 단위(50+ 막대)는 데이터라벨을 완전히 숨기고 툴팁으로만 안내한다.
                // 그 외에는 라벨 충돌 방지: 자동 숨김(display:'auto') + 작은 폰트 + 막대 바깥쪽 오프셋.
                display: isDense ? false : 'auto',
                anchor: 'end',
                align: 'end',
                offset: 4,
                clamp: true,
                color: '#4B5158',
                font: { size: 10 },
                formatter: (value) => {
                  if (value === null || value === undefined || value === 0) return '';
                  const pct = percentageOf(value).toFixed(0);
                  return `${formatCommaNumber(value)} (${pct}%)`;
                },
              },
            },
            scales: {
              x: {
                stacked: false,
                grid: { display: false },
                ticks: {
                  color: '#6e747c',
                  font: { size: isDense ? 10 : 12 },
                  autoSkip: true,
                  maxRotation: 0,
                  minRotation: 0,
                },
              },
              y: {
                beginAtZero: true,
                grid: { color: '#f1f2f3', drawBorder: false },
                ticks: { color: '#6e747c', precision: 0 },
              },
            },
        };

        // (회귀 수정) "warm" 재렌더(체크박스 토글 등)는 destroy 후 재생성 대신 기존 인스턴스를
        // 제자리 갱신한다. 데이터셋 개수가 바뀌어도(체크 시 2개/해제 시 3개) data.labels·
        // data.datasets·options를 통째로 교체 후 update()하면 chart.js v4가 올바르게 반영한다.
        const sameCanvas = this.chartInstance && this.chartInstance.canvas === this.$refs.canvasRef;
        if (sameCanvas) {
          this.chartInstance.data.labels = labels;
          this.chartInstance.data.datasets = datasets;
          this.chartInstance.options = chartOptions;
          this.chartInstance.update();
        } else {
          if (this.chartInstance) {
            this.chartInstance.destroy();
            this.chartInstance = null;
          }
          // (근본 수정) this.chartInstance 참조가 유실된 경우에도(예: 이전 렌더에서 예외로
          // this.chartInstance만 null로 정리된 경우) 해당 canvas에 등록된 chart.js 인스턴스가
          // 남아있으면 "Canvas is already in use" 예외가 난다 — 레지스트리에서 직접 조회해 제거한다.
          const existing = Chart.getChart(this.$refs.canvasRef);
          if (existing) existing.destroy();
          const ctx = this.$refs.canvasRef.getContext('2d');
          // markRaw: data()에 두지 않아 이미 비반응형이지만, 향후 리팩터로 실수 유입되는 것까지
          // 방어하기 위해 이중으로 markRaw 처리한다(Vue 3 + chart.js 무한 재귀 회귀 방지).
          this.chartInstance = markRaw(new Chart(ctx, {
            type: 'bar',
            data: { labels, datasets },
            plugins: [ChartDataLabels],
            options: chartOptions,
          }));
        }
      } catch (error) {
        // 렌더 실패 시 크래시 없이 로그만 남기고 인스턴스를 정리한다(빈 캔버스 방치 방지).
        console.error('[dashboard] renderChart failed', error);
        // (근본 수정) chartInstance를 그냥 null로 버리면 canvas에는 여전히 살아있는 chart.js
        // 인스턴스가 등록되어 있어 이벤트 리스너가 orphan으로 남고("Cannot read properties of
        // undefined" 스팸), 다음 cold create가 "Canvas is already in use"로 실패한다.
        try {
          const orphan = Chart.getChart(this.$refs.canvasRef);
          if (orphan) orphan.destroy();
        } catch (cleanupError) {
          // 정리 실패는 무시한다(원본 오류가 이미 로그됨).
        }
        this.chartInstance = null;
      }
    },
  },
};
</script>

<style scoped>
/* 다른 행(요약/순위)과 동일하게 명시적 width:100%로 고정해 .dashboard-row 컨테이너의
   좌우 경계와 픽셀 단위로 일치시킨다. */
.demographics-widget {
  width: 100%;
  box-sizing: border-box;
}

.demographics-widget-header {
  margin-bottom: 14px;
}

.demographics-header-top {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
}

.demographics-header-top h3 {
  margin-bottom: 0;
}

.demographics-widget-note {
  font-size: 13px;
  color: #8d939a;
  white-space: nowrap;
}

.demographics-controls {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  column-gap: 20px;
  row-gap: 8px;
  margin-top: 12px;
}

.demographics-control-group {
  display: flex;
  align-items: center;
  column-gap: 8px;
}

.demographics-control-label {
  font-size: 13px;
  color: #8d939a;
  white-space: nowrap;
}

/* a-radio-group(size="small")과 같은 행에서 높이·수직 정렬이 그대로 맞도록
   별도 폭만 지정한다(둘 다 antd small 사이즈 컨트롤이라 기본 높이 24px로 동일). */
.demographics-custom-bucket-select {
  width: 84px;
}

/* Ant Design Vue 4 Checkbox에는 size prop이 없어, 옆의 size small 컨트롤
   (demographics-control-label 13px)과 시각적 크기를 CSS로 정합시킨다. */
.demographics-control-group--exclude-unknown :deep(.ant-checkbox-wrapper) {
  font-size: 13px;
  color: #8d939a;
  line-height: 24px;
}

.demographics-control-group--exclude-unknown :deep(.ant-checkbox) {
  transform: scale(0.9);
}

/* Chart.js 리사이즈 진동(캔버스↔부모 높이 상호 의존) 방지 표준 패턴:
   부모를 position:relative + 고정 height로 두고, 캔버스는 absolute로 그 안을
   꽉 채우게 해 캔버스 크기 변화가 부모 높이에 되먹임되지 않도록 한다. */
.demographics-chart-wrap {
  position: relative;
  width: 100%;
  height: 320px;
}

.demographics-chart-wrap canvas {
  position: absolute;
  top: 0;
  left: 0;
  width: 100% !important;
  height: 100% !important;
}

.demographics-footer {
  display: flex;
  align-items: center;
  column-gap: 6px;
  margin-top: 14px;
  font-size: 13px;
  color: #8d939a;
}

.demographics-footer i {
  cursor: pointer;
  color: #acb2b9;
}
</style>
